import React, { useCallback, useEffect, useState } from 'react';
import { fetchSessions, revokeSession, SessionInfo } from '../../utils/authApi';

interface SessionsModalProps {
  onClose: () => void;
}

function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

const SessionsModal: React.FC<SessionsModalProps> = ({ onClose }) => {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSessions();
      setSessions(result);
    } catch {
      setError('Could not load active sessions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (sessionId: string) => {
    setRevokingId(sessionId);
    try {
      await revokeSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
    } catch {
      setError('Could not revoke that session. Please try again.');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900 flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Active devices
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Devices currently signed in to your account.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="toolbar-button"
            aria-label="Close active devices"
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading sessions…
            </div>
          ) : error && sessions.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          ) : sessions.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              No active sessions found.
            </div>
          ) : (
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Device / Browser</th>
                  <th className="px-3 py-2 text-left font-semibold">IP Address</th>
                  <th className="px-3 py-2 text-left font-semibold">Last used</th>
                  <th className="px-3 py-2 text-left font-semibold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {sessions.map((session) => (
                  <tr
                    key={session.sessionId}
                    className={session.isCurrent ? 'bg-sky-50/70 dark:bg-sky-950/40' : undefined}
                  >
                    <td
                      className="px-3 py-2 max-w-[280px] truncate"
                      title={session.userAgent ?? undefined}
                    >
                      {session.deviceName || session.userAgent || 'Unknown device'}
                      {session.isCurrent && (
                        <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-700 dark:bg-sky-900 dark:text-sky-200">
                          This device
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono">
                      {session.ipAddress ?? '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatTimestamp(session.lastUsedAt)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {!session.isCurrent && (
                        <button
                          type="button"
                          disabled={revokingId === session.sessionId}
                          onClick={() => handleRevoke(session.sessionId)}
                          className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                        >
                          {revokingId === session.sessionId ? 'Revoking…' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {error && sessions.length > 0 && (
            <div className="px-5 py-2 text-xs text-red-600 dark:text-red-400">{error}</div>
          )}
        </div>

        <div className="shrink-0 flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionsModal;
